import { useEffect, useState } from "react";
import API, { getErrorMessage } from "../api/axios";
import { useToast } from "../components/Toast";
import {
  Box,
  Paper,
  Typography,
  TableCell,
  TableRow,
  Button,
  Stack,
  TextField,
  IconButton,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import { ACCENT } from "../theme";
import PageContainer from "../components/ui/PageContainer";
import DataTable from "../components/ui/DataTable";
import EmptyState from "../components/ui/EmptyState";

import SearchInput from "../components/ui/SearchInput";

const emptyCompany = {
  name: "",
  email: "",
  phone: "",
  address: "",
  industry: "",
};
const emptySpoc = { name: "", email: "", phone: "", password: "" };

export default function Clients() {
  const toast = useToast();

  const [companies, setCompanies] = useState([]);
  const [spocs, setSpocs] = useState([]);
  const [selected, setSelected] = useState(null); // selected company object
  const [loading, setLoading] = useState(true);

  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [companyForm, setCompanyForm] = useState(emptyCompany);
  const [savingCompany, setSavingCompany] = useState(false);

  const [showSpocForm, setShowSpocForm] = useState(false);
  const [spocForm, setSpocForm] = useState(emptySpoc);
  const [savingSpoc, setSavingSpoc] = useState(false);

  // ── Phase 11 (Design Gap Audit item K): visual-parity additions ──────────
  // Search box for the company list (client-side filter, no API change).
  const [companySearch, setCompanySearch] = useState("");

  // Real per-company open-role counts, reusing the EXISTING /jobs endpoint
  // (already called by JobRequirements.jsx) rather than adding a new
  // backend field/endpoint. SPOC counts per row are intentionally NOT
  // added — that would require one /clients/{id}/spocs call PER company
  // (an N+1 request pattern), which isn't worth it for a count display.
  const [jobs, setJobs] = useState([]);
  useEffect(() => {
    API.get("/jobs")
      .then((r) => setJobs(r.data || []))
      .catch(() => setJobs([])); // non-critical — page still works without it
  }, []);
  const openRolesFor = (companyName) =>
    jobs.filter(
      (j) =>
        j.companyName === companyName && (j.status || "ACTIVE") !== "CLOSED",
    ).length;
  const totalOpenRoles = jobs.filter(
    (j) => (j.status || "ACTIVE") !== "CLOSED",
  ).length;

  const filteredCompanies = companies.filter((c) =>
    (c.name || "").toLowerCase().includes(companySearch.toLowerCase()),
  );

  const AVATAR_COLORS = [
    "#2563eb",
    "#7c3aed",
    "#0d9488",
    "#d97706",
    "#dc2626",
    "#4f46e5",
    "#0891b2",
    "#65a30d",
  ];
  const initials = (name) =>
    (name || "")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?";

  // ── Load companies ──
  const loadCompanies = () => {
    setLoading(true);
    API.get("/clients")
      .then((r) => {
        setCompanies(r.data || []);
        // keep selection if still present, else pick the first
        setSelected((prev) => {
          if (prev && (r.data || []).some((c) => c.id === prev.id)) return prev;
          return (r.data || [])[0] || null;
        });
      })
      .catch(() => toast.error("Could not load companies"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load SPOCs for the selected company ──
  useEffect(() => {
    if (!selected) {
      setSpocs([]);
      return;
    }
    API.get(`/clients/${selected.id}/spocs`)
      .then((r) => setSpocs(r.data || []))
      .catch(() => setSpocs([]));
  }, [selected]);

  // ── Add company ──
  const addCompany = async () => {
    if (!companyForm.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSavingCompany(true);
    try {
      const res = await API.post("/clients", {
        ...companyForm,
        name: companyForm.name.trim(),
      });
      toast.success("Company added");
      setCompanyForm(emptyCompany);
      setShowCompanyForm(false);
      loadCompanies();
      setSelected(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not add company"));
    } finally {
      setSavingCompany(false);
    }
  };

  // ── Delete company ──
  const deleteCompany = async (c) => {
    if (
      !window.confirm(
        `Delete "${c.name}" and all its SPOCs? This cannot be undone.`,
      )
    )
      return;
    try {
      await API.delete(`/clients/${c.id}`);
      toast.success("Company deleted");
      if (selected?.id === c.id) setSelected(null);
      loadCompanies();
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not delete company"));
    }
  };

  // ── Add SPOC ──
  const addSpoc = async () => {
    if (!selected) return;
    if (!spocForm.name.trim()) {
      toast.error("SPOC name is required");
      return;
    }
    if (!spocForm.email.trim()) {
      toast.error("Email is required so the SPOC can log in");
      return;
    }
    if (!spocForm.password || spocForm.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSavingSpoc(true);
    try {
      await API.post(`/clients/${selected.id}/spocs`, {
        ...spocForm,
        name: spocForm.name.trim(),
        email: spocForm.email.trim(),
      });
      toast.success("SPOC added — they can now log in with this email");
      setSpocForm(emptySpoc);
      setShowSpocForm(false);
      const r = await API.get(`/clients/${selected.id}/spocs`);
      setSpocs(r.data || []);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not add SPOC"));
    } finally {
      setSavingSpoc(false);
    }
  };

  // ── Delete SPOC ──
  const deleteSpoc = async (s) => {
    if (!window.confirm(`Delete SPOC "${s.name}"?`)) return;
    try {
      await API.delete(`/clients/spocs/${s.id}`);
      toast.success("SPOC deleted");
      setSpocs((prev) => prev.filter((x) => x.id !== s.id));
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not delete SPOC"));
    }
  };

  return (
    <PageContainer sx={{ px: { xs: 2, md: 2.5 }, py: { xs: 2, md: 2 } }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2, pl: { xs: 5, md: 0 } }}
      >
        <Typography
          sx={{ color: "text.secondary", fontSize: 14, fontWeight: 600 }}
        >
          {companies.length} client{" "}
          {companies.length === 1 ? "company" : "companies"}
          {jobs.length > 0 &&
            ` · ${totalOpenRoles} open role${totalOpenRoles !== 1 ? "s" : ""}`}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowCompanyForm((v) => !v)}
        >
          Add Company
        </Button>
      </Stack>

      {/* Add company form */}
      {showCompanyForm && (
        <Paper sx={{ p: 2.5, mb: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 2 }}>New Company</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label={
                  <span>
                    Company Name<span style={{ color: "#dc2626" }}> *</span>
                  </span>
                }
                value={companyForm.name}
                onChange={(e) =>
                  setCompanyForm({ ...companyForm, name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Email"
                value={companyForm.email}
                onChange={(e) =>
                  setCompanyForm({ ...companyForm, email: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Phone"
                value={companyForm.phone}
                onChange={(e) =>
                  setCompanyForm({ ...companyForm, phone: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Industry"
                value={companyForm.industry}
                onChange={(e) =>
                  setCompanyForm({ ...companyForm, industry: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Address"
                value={companyForm.address}
                onChange={(e) =>
                  setCompanyForm({ ...companyForm, address: e.target.value })
                }
              />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={addCompany}
              disabled={savingCompany}
            >
              {savingCompany ? "Saving…" : "Save Company"}
            </Button>
            <Button
              color="inherit"
              onClick={() => {
                setShowCompanyForm(false);
                setCompanyForm(emptyCompany);
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Paper>
      )}

      <Grid container spacing={2}>
        {/* Companies list */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ overflow: "hidden" }}>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <BusinessOutlinedIcon fontSize="small" color="primary" />
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                Companies
              </Typography>
            </Box>
            <Box
              sx={{ p: 1, borderBottom: "1px solid", borderColor: "divider" }}
            >
              <SearchInput
                value={companySearch}
                onChange={setCompanySearch}
                placeholder="Search clients"
              />
            </Box>
            <DataTable
              columns={[
                { key: "name", label: "Name" },
                { key: "roles", label: "Open roles" },
                { key: "actions", label: "Actions", align: "right" },
              ]}
              rows={filteredCompanies}
              getRowKey={(c) => c.id}
              loading={loading}
              loadingVariant="rows"
              maxHeight="calc(100vh - 290px)"
              emptyTitle="No companies found"
              emptyDescription={
                companySearch
                  ? "Try a different search."
                  : 'Click "Add Company" to get started.'
              }
              renderRow={(c) => (
                <TableRow
                  key={c.id}
                  hover
                  selected={selected?.id === c.id}
                  onClick={() => setSelected(c)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: "7px",
                          bgcolor:
                            AVATAR_COLORS[c.id % AVATAR_COLORS.length] ||
                            ACCENT,
                          color: "#fff",
                          fontSize: 11,
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {initials(c.name)}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>
                          {c.name}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 11.5, color: "text.secondary" }}
                        >
                          {c.industry || "—"}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>{openRolesFor(c.name)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteCompany(c);
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )}
            />
          </Paper>
        </Grid>

        {/* SPOCs for selected company */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ overflow: "hidden" }}>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {selected && (
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "8px",
                      bgcolor:
                        AVATAR_COLORS[selected.id % AVATAR_COLORS.length] ||
                        ACCENT,
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {initials(selected.name)}
                  </Box>
                )}
                <ContactsOutlinedIcon
                  fontSize="small"
                  color="primary"
                  sx={{ display: selected ? "none" : "block" }}
                />
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                  {selected ? `SPOCs — ${selected.name}` : "SPOCs"}
                </Typography>
              </Box>
              {selected && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => setShowSpocForm((v) => !v)}
                >
                  Add SPOC
                </Button>
              )}
            </Box>

            {!selected ? (
              <EmptyState
                icon={
                  <ContactsOutlinedIcon sx={{ fontSize: 28, color: ACCENT }} />
                }
                title="Select a company"
                description="Choose a company from the list to view its SPOCs."
              />
            ) : (
              <>
                {/* Phase 11: one honest, real KPI — Filled(90d)/Avg-time-to-fill/
                    SLA-breaches from the prototype are intentionally NOT shown
                    here, since none are reliably derivable from existing data
                    (no per-stage timestamps, no "filled" event tracking). */}
                <Box sx={{ px: 2, pt: 1.5 }}>
                  <Box
                    sx={{
                      display: "inline-block",
                      bgcolor: "action.hover",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "8px",
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                      Open roles
                    </Typography>
                    <Typography sx={{ fontSize: 19, fontWeight: 800 }}>
                      {openRolesFor(selected.name)}
                    </Typography>
                  </Box>
                </Box>

                {showSpocForm && (
                  <Box
                    sx={{
                      p: 2,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label={
                            <span>
                              SPOC Name
                              <span style={{ color: "#dc2626" }}> *</span>
                            </span>
                          }
                          value={spocForm.name}
                          onChange={(e) =>
                            setSpocForm({ ...spocForm, name: e.target.value })
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label={
                            <span>
                              Email
                              <span style={{ color: "#dc2626" }}> *</span>
                            </span>
                          }
                          value={spocForm.email}
                          onChange={(e) =>
                            setSpocForm({ ...spocForm, email: e.target.value })
                          }
                          helperText="Used as their login username"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Phone"
                          value={spocForm.phone}
                          onChange={(e) =>
                            setSpocForm({ ...spocForm, phone: e.target.value })
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          type="password"
                          label={
                            <span>
                              Password
                              <span style={{ color: "#dc2626" }}> *</span>
                            </span>
                          }
                          value={spocForm.password}
                          onChange={(e) =>
                            setSpocForm({
                              ...spocForm,
                              password: e.target.value,
                            })
                          }
                          helperText="At least 6 characters — they can change it later in Settings"
                        />
                      </Grid>
                    </Grid>
                    <Typography
                      sx={{ fontSize: 12, color: "text.secondary", mt: 1 }}
                    >
                      This creates a Client login account for {selected?.name}{" "}
                      with the details above.
                    </Typography>
                    <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={addSpoc}
                        disabled={savingSpoc}
                      >
                        {savingSpoc ? "Saving…" : "Save SPOC"}
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => {
                          setShowSpocForm(false);
                          setSpocForm(emptySpoc);
                        }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Box>
                )}

                <DataTable
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    { key: "phone", label: "Phone" },
                    { key: "actions", label: "Actions", align: "right" },
                  ]}
                  rows={spocs}
                  getRowKey={(s) => s.id}
                  maxHeight="calc(100vh - 300px)"
                  emptyTitle="No SPOCs yet for this company"
                  renderRow={(s) => (
                    <TableRow key={s.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                      <TableCell>{s.email || "—"}</TableCell>
                      <TableCell>{s.phone || "—"}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => deleteSpoc(s)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )}
                />
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </PageContainer>
  );
}
